import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import CrudModal from "../../components/CrudModal";
import { toast } from "sonner";
import {
  Books, VideoCamera, FileText, Translate, PencilSimple, Trash, Plus, ClipboardText,
  Stack, UploadSimple, Target, Question, GraduationCap, FilePdf, FileVideo, FileAudio,
  Globe, CaretRight, CaretDown, CheckCircle, Download, Broadcast, Sparkle,
} from "@phosphor-icons/react";
import AIQuizGenerator from "../../components/AIQuizGenerator";

const TYPE_CLS = {
  Core: "bg-[hsl(244_53%_20%)] text-white",
  Multidisciplinary: "bg-[hsl(20_90%_48%)] text-white",
  Elective: "bg-secondary text-foreground",
  Project: "bg-emerald-50 text-emerald-700",
};

const COURSE_FIELDS_NEW = [
  { name: "course_id", label: "Course ID (e.g. CS305)", type: "text" },
  { name: "title", label: "Course title", type: "text" },
  { name: "faculty", label: "Faculty", type: "text" },
  { name: "credits", label: "Credits", type: "number" },
  { name: "nep_credits", label: "NEP credits", type: "number" },
  { name: "students", label: "# Students enrolled", type: "number" },
  { name: "completion", label: "Completion %", type: "number" },
  { name: "type", label: "Type", type: "select", options: [
    { value: "Core", label: "Core" }, { value: "Multidisciplinary", label: "Multidisciplinary" },
    { value: "Elective", label: "Elective" }, { value: "Project", label: "Project" },
  ]},
];
const COURSE_FIELDS_EDIT = COURSE_FIELDS_NEW.filter(f => f.name !== "course_id");

const ASG_FIELDS = [
  { name: "course_id", label: "Course ID", type: "text" },
  { name: "title", label: "Assignment title", type: "text" },
  { name: "due", label: "Due date (YYYY-MM-DD)", type: "text" },
  { name: "submissions", label: "Submissions received", type: "number" },
  { name: "total_students", label: "Total students", type: "number" },
  { name: "status", label: "Status", type: "select", options: [
    { value: "Open", label: "Open" }, { value: "Grading", label: "Grading" }, { value: "Closed", label: "Closed" },
  ]},
];

const MODULE_FIELDS = (courseOptions) => [
  { name: "course_id", label: "Course", type: "select", options: courseOptions },
  { name: "title", label: "Module title", type: "text" },
  { name: "order", label: "Order", type: "number" },
  { name: "status", label: "Status", type: "select", options: [
    { value: "draft", label: "Draft" }, { value: "review", label: "Review" },
    { value: "published", label: "Published" }, { value: "archived", label: "Archived" },
  ]},
];

const LESSON_FIELDS = (modules) => [
  { name: "module_id", label: "Parent module", type: "select", options: modules.map(m => ({ value: m.module_id, label: `${m.course_id} — ${m.title}` })) },
  { name: "title", label: "Lesson title", type: "text" },
  { name: "order", label: "Order", type: "number" },
  { name: "content_type", label: "Content type", type: "select", options: [
    { value: "text", label: "Text/Reading" }, { value: "video", label: "Video" }, { value: "pdf", label: "PDF" },
    { value: "audio", label: "Audio" }, { value: "html5", label: "HTML5" }, { value: "scorm", label: "SCORM" }, { value: "url", label: "URL/Link" },
  ]},
  { name: "content_url", label: "Content URL (optional)", type: "text" },
  { name: "duration_min", label: "Duration (min)", type: "number" },
  { name: "bloom_level", label: "Bloom level", type: "select", options: [
    { value: "Remember", label: "Remember" }, { value: "Understand", label: "Understand" },
    { value: "Apply", label: "Apply" }, { value: "Analyze", label: "Analyze" },
    { value: "Evaluate", label: "Evaluate" }, { value: "Create", label: "Create" },
  ]},
];

const OUTCOME_FIELDS = (courseOptions) => [
  { name: "course_id", label: "Course", type: "select", options: courseOptions },
  { name: "code", label: "Code (e.g. CO1, PO2)", type: "text" },
  { name: "type", label: "Type", type: "select", options: [
    { value: "CO", label: "CO — Course Outcome" }, { value: "PO", label: "PO — Program Outcome" },
    { value: "PSO", label: "PSO" }, { value: "Competency", label: "Competency" },
  ]},
  { name: "description", label: "Description", type: "textarea" },
];

const QUIZ_FIELDS = (courseOptions) => [
  { name: "course_id", label: "Course", type: "select", options: courseOptions },
  { name: "title", label: "Quiz title", type: "text" },
  { name: "time_limit_min", label: "Time limit (min)", type: "number" },
  { name: "passing_score", label: "Passing score %", type: "number" },
  { name: "mode", label: "Mode", type: "select", options: [
    { value: "practice", label: "Practice" }, { value: "graded", label: "Graded" }, { value: "proctored", label: "Proctored" },
  ]},
];

const QUESTION_FIELDS = (quizOptions) => [
  { name: "quiz_id", label: "Quiz", type: "select", options: quizOptions },
  { name: "text", label: "Question text", type: "textarea" },
  { name: "type", label: "Type", type: "select", options: [
    { value: "MCQ", label: "MCQ (single)" }, { value: "MSQ", label: "MSQ (multi)" },
    { value: "TF", label: "True/False" }, { value: "SHORT", label: "Short answer" }, { value: "NUMERIC", label: "Numeric" },
  ]},
  { name: "options_csv", label: "Options (comma-separated)", type: "text", hint: "e.g. Option A, Option B, Option C" },
  { name: "correct_csv", label: "Correct answer(s) (comma-separated)", type: "text" },
  { name: "marks", label: "Marks", type: "number" },
  { name: "outcome_code", label: "Linked outcome code (optional)", type: "text" },
];

const LIVE_FIELDS = (courseOptions) => [
  { name: "course_id", label: "Course", type: "select", options: courseOptions },
  { name: "title", label: "Session title", type: "text" },
  { name: "start_at", label: "Start at (ISO 8601)", type: "text", hint: "e.g. 2026-03-15T10:00:00Z" },
  { name: "duration_min", label: "Duration (min)", type: "number" },
  { name: "join_url", label: "Join URL", type: "text" },
  { name: "provider", label: "Provider", type: "select", options: [
    { value: "meet", label: "Google Meet" }, { value: "zoom", label: "Zoom" }, { value: "teams", label: "MS Teams" }, { value: "native", label: "Native" },
  ]},
];

const SUBMIT_FIELDS = [
  { name: "student_name", label: "Student name", type: "text" },
  { name: "student_id", label: "Student ID", type: "text" },
  { name: "text", label: "Answer / notes", type: "textarea" },
];

const GRADE_FIELDS = [
  { name: "score", label: "Score", type: "number" },
  { name: "feedback", label: "Feedback", type: "textarea" },
  { name: "status", label: "Status", type: "select", options: [
    { value: "graded", label: "Graded" }, { value: "returned", label: "Returned" }, { value: "pending", label: "Pending" },
  ]},
];

const CTYPE_ICON = {
  video: FileVideo, pdf: FilePdf, audio: FileAudio, html5: Globe, scorm: Globe, url: Globe, text: FileText,
};

export default function LMS() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [tab, setTab] = useState("builder");
  const [courses, setCourses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [submissions, setSubmissions] = useState({});  // assignment_id -> [submissions]
  const [expanded, setExpanded] = useState({});  // course_id -> bool
  const [selectedAsg, setSelectedAsg] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [modal, setModal] = useState({ open: false, kind: null, editing: null, extra: {} });
  const [uploading, setUploading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const fileRef = useRef(null);
  const [uploadCourse, setUploadCourse] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, a, m, l, o, ast, q, qq, lv] = await Promise.all([
        api.get("/courses"),
        api.get("/assignments"),
        api.get("/lms/modules"),
        api.get("/lms/lessons"),
        api.get("/lms/outcomes"),
        api.get("/lms/assets"),
        api.get("/lms/quizzes"),
        api.get("/lms/questions"),
        api.get("/lms/live"),
      ]);
      setCourses(c.data); setAssignments(a.data); setModules(m.data); setLessons(l.data);
      setOutcomes(o.data); setAssets(ast.data); setQuizzes(q.data); setQuestions(qq.data);
      setLiveSessions(lv.data);
    } catch (err) {
      toast.error("Failed to load LMS data");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const courseOptions = courses.map(c => ({ value: c.course_id, label: `${c.course_id} — ${c.title}` }));
  const quizOptions = quizzes.map(q => ({ value: q.quiz_id, label: q.title }));

  const openCreate = (kind, extra = {}) => setModal({ open: true, kind, editing: null, extra });
  const openEdit = (kind, row, extra = {}) => setModal({ open: true, kind, editing: row, extra });
  const close = () => setModal({ open: false, kind: null, editing: null, extra: {} });

  const save = async (values) => {
    try {
      const k = modal.kind;
      if (k === "course") {
        if (modal.editing) { await api.patch(`/courses/${modal.editing.course_id}`, values); toast.success("Course updated"); }
        else { await api.post("/courses", values); toast.success("Course created"); }
      } else if (k === "assignment") {
        if (modal.editing) {
          const { course_id, ...patch } = values;
          await api.patch(`/assignments/${modal.editing.assignment_id}`, patch);
          toast.success("Assignment updated");
        } else { await api.post("/assignments", values); toast.success("Assignment created"); }
      } else if (k === "module") {
        await api.post("/lms/modules", values); toast.success("Module created");
      } else if (k === "lesson") {
        const course_id = modules.find(m => m.module_id === values.module_id)?.course_id;
        await api.post("/lms/lessons", { ...values, course_id }); toast.success("Lesson created");
      } else if (k === "outcome") {
        await api.post("/lms/outcomes", values); toast.success("Outcome added");
      } else if (k === "quiz") {
        await api.post("/lms/quizzes", values); toast.success("Quiz created");
      } else if (k === "question") {
        const payload = {
          quiz_id: values.quiz_id, text: values.text, type: values.type, marks: Number(values.marks) || 1,
          options: (values.options_csv || "").split(",").map(s => s.trim()).filter(Boolean),
          correct: (values.correct_csv || "").split(",").map(s => s.trim()).filter(Boolean),
          outcome_code: values.outcome_code || null,
        };
        await api.post("/lms/questions", payload); toast.success("Question added");
      } else if (k === "live") {
        await api.post("/lms/live", values); toast.success("Live session scheduled");
      } else if (k === "submission") {
        await api.post("/lms/submissions", { ...values, assignment_id: modal.extra.assignment_id });
        toast.success("Submission recorded");
        loadSubmissions(modal.extra.assignment_id);
      } else if (k === "grade") {
        await api.patch(`/lms/submissions/${modal.editing.submission_id}/grade`, {
          score: Number(values.score) || 0, feedback: values.feedback, status: values.status || "graded",
        });
        toast.success("Graded");
        loadSubmissions(modal.editing.assignment_id);
      }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
  };

  const removeCourse = async (c) => {
    if (!window.confirm(`Delete course ${c.course_id} — ${c.title}?`)) return;
    try { await api.delete(`/courses/${c.course_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const removeAsg = async (a) => {
    if (!window.confirm(`Delete assignment "${a.title}"?`)) return;
    try { await api.delete(`/assignments/${a.assignment_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const removeModule = async (m) => {
    if (!window.confirm(`Delete module "${m.title}" and all its lessons?`)) return;
    try { await api.delete(`/lms/modules/${m.module_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const removeLesson = async (l) => {
    if (!window.confirm(`Delete lesson "${l.title}"?`)) return;
    try { await api.delete(`/lms/lessons/${l.lesson_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const removeOutcome = async (o) => {
    if (!window.confirm(`Delete outcome ${o.code}?`)) return;
    try { await api.delete(`/lms/outcomes/${o.course_id}/${o.code}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const removeQuiz = async (q) => {
    if (!window.confirm(`Delete quiz "${q.title}" and all questions?`)) return;
    try { await api.delete(`/lms/quizzes/${q.quiz_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const removeQuestion = async (q) => {
    try { await api.delete(`/lms/questions/${q.question_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const removeLive = async (s) => {
    if (!window.confirm(`Cancel live session "${s.title}"?`)) return;
    try { await api.delete(`/lms/live/${s.session_id}`); toast.success("Cancelled"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const removeAsset = async (a) => {
    if (!window.confirm(`Delete asset "${a.title}"?`)) return;
    try { await api.delete(`/lms/assets/${a.asset_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const params = new URLSearchParams();
      if (uploadCourse) params.set("course_id", uploadCourse);
      params.set("title", file.name);
      await api.post(`/lms/assets/upload?${params.toString()}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const loadSubmissions = async (assignment_id) => {
    try {
      const r = await api.get(`/lms/submissions?assignment_id=${assignment_id}`);
      setSubmissions(s => ({ ...s, [assignment_id]: r.data }));
    } catch { /* ignore */ }
  };

  const toggleExpand = (course_id) => setExpanded(e => ({ ...e, [course_id]: !e[course_id] }));

  const tabs = [
    { v: "builder", l: t("lms.tabs.builder"), I: Stack },
    { v: "library", l: t("lms.tabs.library"), I: UploadSimple },
    { v: "outcomes", l: t("lms.tabs.outcomes"), I: Target },
    { v: "assignments", l: t("lms.tabs.assignments"), I: ClipboardText },
    { v: "quizzes", l: t("lms.tabs.quizzes"), I: Question },
    { v: "live", l: t("lms.tabs.live"), I: Broadcast },
    { v: "courses", l: t("lms.tabs.catalog"), I: Books },
  ];

  return (
    <div>
      <PageHeader
        overline="M06 · Academic Spine"
        title={t("lms.title")}
        description={t("lms.subtitle")}
        action={
          <div className="flex items-center gap-2">
            {tab === "builder" && (
              <>
                <Button onClick={() => openCreate("module")} variant="outline" className="rounded-md" data-testid="new-module-btn">
                  <Plus size={14} className="mr-1.5"/> Module
                </Button>
                <Button onClick={() => openCreate("course")} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-lms-btn">
                  <Plus size={14} className="mr-1.5"/> New course
                </Button>
              </>
            )}
            {tab === "library" && (
              <>
                <select value={uploadCourse} onChange={e => setUploadCourse(e.target.value)} className="px-2 py-1.5 text-sm border border-border rounded-md bg-white" data-testid="upload-course-select">
                  <option value="">All courses</option>
                  {courses.map(c => <option key={c.course_id} value={c.course_id}>{c.course_id}</option>)}
                </select>
                <input ref={fileRef} type="file" onChange={handleUpload} className="hidden" data-testid="upload-file-input"/>
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="upload-btn">
                  <UploadSimple size={14} className="mr-1.5"/> {uploading ? "Uploading…" : "Upload file"}
                </Button>
              </>
            )}
            {tab === "outcomes" && (
              <Button onClick={() => openCreate("outcome")} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-outcome-btn">
                <Plus size={14} className="mr-1.5"/> New outcome
              </Button>
            )}
            {tab === "assignments" && (
              <Button onClick={() => openCreate("assignment")} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-asg-btn">
                <Plus size={14} className="mr-1.5"/> New assignment
              </Button>
            )}
            {tab === "quizzes" && (
              <>
                {selectedQuiz && (
                  <Button onClick={() => openCreate("question", { quiz_id: selectedQuiz })} variant="outline" className="rounded-md" data-testid="new-question-btn">
                    <Plus size={14} className="mr-1.5"/> Question
                  </Button>
                )}
                <Button onClick={() => setAiOpen(true)} variant="outline" className="rounded-md border-[hsl(244_53%_20%)] text-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_20%)] hover:text-white" data-testid="ai-quiz-btn">
                  <Sparkle size={14} weight="fill" className="mr-1.5 text-[hsl(20_90%_55%)]"/> {t("lms.ai_generate")}
                </Button>
                <Button onClick={() => openCreate("quiz")} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-quiz-btn">
                  <Plus size={14} className="mr-1.5"/> New quiz
                </Button>
              </>
            )}
            {tab === "live" && (
              <Button onClick={() => openCreate("live")} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-live-btn">
                <Plus size={14} className="mr-1.5"/> Schedule session
              </Button>
            )}
            {tab === "courses" && (
              <Button onClick={() => openCreate("course")} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-course-btn">
                <Plus size={14} className="mr-1.5"/> New course
              </Button>
            )}
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-6">

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
          {[
            { I: Books, l: "Active courses", v: courses.length, t: "current term" },
            { I: Stack, l: "Modules / Lessons", v: `${modules.length}/${lessons.length}`, t: "across all courses" },
            { I: UploadSimple, l: "Content assets", v: assets.length, t: "PDFs, videos, decks" },
            { I: Target, l: "Mapped outcomes", v: outcomes.length, t: "CO + PO + PSO" },
          ].map(s => (
            <div key={s.l} className="bg-white p-5">
              <s.I size={20} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
              <div className="eos-overline mt-3">{s.l}</div>
              <div className="font-heading font-extrabold text-3xl mt-1 tracking-tight">{s.v}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{s.t}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="inline-flex flex-wrap border border-border rounded-md overflow-hidden" data-testid="lms-tabs">
          {tabs.map(t => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 border-r border-border last:border-r-0 ${tab === t.v ? "bg-[hsl(244_53%_20%)] text-white" : "bg-white hover:bg-secondary"}`}
              data-testid={`lms-tab-${t.v}`}
            >
              <t.I size={14}/> {t.l}
            </button>
          ))}
        </div>

        {/* ----- Course Builder ----- */}
        {tab === "builder" && (
          <div className="space-y-3" data-testid="builder-pane">
            {courses.length === 0 && <div className="eos-card text-center text-sm text-muted-foreground">No courses yet. Click "New course" to start.</div>}
            {courses.map(c => {
              const cMods = modules.filter(m => m.course_id === c.course_id).sort((a, b) => a.order - b.order);
              const isOpen = expanded[c.course_id];
              return (
                <div key={c.course_id} className="eos-card p-0 overflow-hidden" data-testid={`builder-course-${c.course_id}`}>
                  <button onClick={() => toggleExpand(c.course_id)} className="w-full flex items-center justify-between p-4 hover:bg-secondary/40">
                    <div className="flex items-center gap-3">
                      {isOpen ? <CaretDown size={14}/> : <CaretRight size={14}/>}
                      <span className={`text-[10px] px-2 py-0.5 rounded-md font-medium ${TYPE_CLS[c.type] || "bg-secondary"}`}>{c.type}</span>
                      <span className="font-mono-eos text-[12px] text-muted-foreground">{c.course_id}</span>
                      <span className="font-heading font-bold text-base">{c.title}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span>{cMods.length} modules</span>
                      <span>{lessons.filter(l => l.course_id === c.course_id).length} lessons</span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-border bg-secondary/20 p-4 space-y-3">
                      {cMods.length === 0 && (
                        <div className="text-sm text-muted-foreground italic">No modules yet. <button onClick={() => openCreate("module", { course_id: c.course_id })} className="underline text-[hsl(244_53%_20%)]" data-testid={`add-module-${c.course_id}`}>Add module</button></div>
                      )}
                      {cMods.map(m => {
                        const mLessons = lessons.filter(l => l.module_id === m.module_id).sort((a, b) => a.order - b.order);
                        return (
                          <div key={m.module_id} className="bg-white border border-border rounded-md p-3" data-testid={`module-${m.module_id}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="font-mono-eos text-[11px] bg-secondary px-1.5 py-0.5 rounded">M{m.order}</span>
                                <span className="font-medium text-sm">{m.title}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{m.status}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" onClick={() => openCreate("lesson", { module_id: m.module_id })} className="h-6 px-2 text-[11px]" data-testid={`add-lesson-${m.module_id}`}>+ Lesson</Button>
                                <Button variant="ghost" onClick={() => removeModule(m)} className="h-6 w-6 p-0 rounded text-rose-600" title="Delete module" data-testid={`del-module-${m.module_id}`}><Trash size={12}/></Button>
                              </div>
                            </div>
                            {mLessons.length > 0 && (
                              <ul className="mt-2 ml-6 space-y-1">
                                {mLessons.map(l => {
                                  const Icon = CTYPE_ICON[l.content_type] || FileText;
                                  return (
                                    <li key={l.lesson_id} className="flex items-center justify-between text-[13px] py-1 border-b last:border-b-0 border-border/50" data-testid={`lesson-${l.lesson_id}`}>
                                      <div className="flex items-center gap-2">
                                        <Icon size={14} className="text-muted-foreground"/>
                                        <span>{l.order}. {l.title}</span>
                                        <span className="text-[10px] text-muted-foreground">· {l.duration_min}min · {l.content_type}</span>
                                        {l.bloom_level && <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{l.bloom_level}</span>}
                                      </div>
                                      <Button variant="ghost" onClick={() => removeLesson(l)} className="h-5 w-5 p-0 rounded text-rose-600" data-testid={`del-lesson-${l.lesson_id}`}><Trash size={10}/></Button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ----- Content Library ----- */}
        {tab === "library" && (
          <div className="space-y-4" data-testid="library-pane">
            {assets.length === 0 && <div className="eos-card text-center text-sm text-muted-foreground">No uploads yet. Use "Upload file" to add PDFs, videos, or decks. Files are stored in Emergent Object Storage.</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {assets.map(a => {
                const ext = (a.original_filename || "").split(".").pop().toLowerCase();
                const Icon = ext === "pdf" ? FilePdf : ext.match(/mp4|mov|webm/) ? FileVideo : ext.match(/mp3|wav|ogg/) ? FileAudio : FileText;
                return (
                  <div key={a.asset_id} className="eos-card eos-card-hover flex items-start gap-3" data-testid={`asset-${a.asset_id}`}>
                    <div className="h-10 w-10 rounded-md bg-[hsl(244_53%_20%)] flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-white" weight="duotone"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{a.title || a.original_filename}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{a.original_filename} · {(a.size / 1024).toFixed(1)} KB · {a.course_id || "—"}</div>
                      <div className="mt-2 flex items-center gap-2">
                        <a href={`${API}/lms/assets/${a.asset_id}/file?auth=eos_test_session_admin_001`} target="_blank" rel="noreferrer" className="text-[12px] underline text-[hsl(244_53%_20%)] inline-flex items-center gap-1" data-testid={`asset-dl-${a.asset_id}`}>
                          <Download size={12}/> View
                        </a>
                        <Button variant="ghost" onClick={() => removeAsset(a)} className="h-6 w-6 p-0 text-rose-600" data-testid={`del-asset-${a.asset_id}`}><Trash size={12}/></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ----- Outcomes ----- */}
        {tab === "outcomes" && (
          <div className="space-y-4" data-testid="outcomes-pane">
            {courses.map(c => {
              const cOuts = outcomes.filter(o => o.course_id === c.course_id);
              return (
                <div key={c.course_id} className="eos-card">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-mono-eos text-[11px] text-muted-foreground">{c.course_id}</div>
                      <div className="font-heading font-bold">{c.title}</div>
                    </div>
                    <span className="text-[11px] text-muted-foreground">{cOuts.length} outcomes</span>
                  </div>
                  {cOuts.length === 0 ? <div className="text-sm text-muted-foreground italic">No outcomes defined.</div> : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {cOuts.map(o => (
                        <div key={`${o.course_id}-${o.code}`} className="flex items-start gap-2 p-2.5 border border-border rounded-md" data-testid={`outcome-${o.course_id}-${o.code}`}>
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${o.type === "CO" ? "bg-indigo-50 text-indigo-700" : o.type === "PO" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{o.type}</span>
                          <span className="font-mono-eos text-[11px] mt-0.5">{o.code}</span>
                          <span className="text-sm flex-1">{o.description}</span>
                          <Button variant="ghost" onClick={() => removeOutcome(o)} className="h-6 w-6 p-0 text-rose-600" data-testid={`del-outcome-${o.code}`}><Trash size={12}/></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {courses.length === 0 && <div className="eos-card text-center text-sm text-muted-foreground">Create courses first to attach outcomes.</div>}
          </div>
        )}

        {/* ----- Assignments ----- */}
        {tab === "assignments" && (
          <div className="space-y-4" data-testid="assignments-pane">
            <div className="eos-card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary text-muted-foreground">
                  <tr>{["Course", "Title", "Due", "Subs", "Status", "Actions"].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {assignments.map(a => (
                    <tr key={a.assignment_id} className={`border-t border-border hover:bg-secondary/40 cursor-pointer ${selectedAsg === a.assignment_id ? "bg-secondary/60" : ""}`} onClick={() => { setSelectedAsg(a.assignment_id); loadSubmissions(a.assignment_id); }} data-testid={`asg-row-${a.assignment_id}`}>
                      <td className="px-4 py-3 font-mono-eos text-[12px]">{a.course_id}</td>
                      <td className="px-4 py-3 font-medium">{a.title}</td>
                      <td className="px-4 py-3 text-muted-foreground">{a.due}</td>
                      <td className="px-4 py-3 font-mono-eos">{a.submissions}/{a.total_students}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] px-2 py-1 rounded-md ${
                          a.status === "Open" ? "bg-emerald-50 text-emerald-700" :
                          a.status === "Grading" ? "bg-amber-50 text-amber-800" : "bg-secondary text-muted-foreground"
                        }`}>{a.status}</span>
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" onClick={() => openCreate("submission", { assignment_id: a.assignment_id })} className="h-7 px-2 text-[11px]" title="Add submission" data-testid={`sub-${a.assignment_id}`}>+ Sub</Button>
                          <Button variant="ghost" onClick={() => openEdit("assignment", a)} className="h-7 w-7 p-0 rounded" title="Edit" data-testid={`edit-${a.assignment_id}`}><PencilSimple size={13}/></Button>
                          <Button variant="ghost" onClick={() => removeAsg(a)} className="h-7 w-7 p-0 rounded text-rose-600" title="Delete" data-testid={`delete-${a.assignment_id}`}><Trash size={13}/></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {assignments.length === 0 && <tr><td colSpan="6" className="p-10 text-center text-sm text-muted-foreground">No assignments yet.</td></tr>}
                </tbody>
              </table>
            </div>

            {selectedAsg && (
              <div className="eos-card" data-testid="submissions-pane">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading font-bold">Submissions — {assignments.find(a => a.assignment_id === selectedAsg)?.title}</div>
                  <span className="text-[11px] text-muted-foreground">{(submissions[selectedAsg] || []).length} submissions</span>
                </div>
                {!(submissions[selectedAsg] || []).length && <div className="text-sm text-muted-foreground italic">No submissions yet. Click "+ Sub" to add one.</div>}
                <div className="space-y-2">
                  {(submissions[selectedAsg] || []).map(s => (
                    <div key={s.submission_id} className="flex items-start justify-between p-3 border border-border rounded-md" data-testid={`submission-${s.submission_id}`}>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{s.student_name} <span className="font-mono-eos text-[11px] text-muted-foreground">({s.student_id})</span></div>
                        {s.text && <div className="text-[12px] text-muted-foreground mt-1 line-clamp-2">{s.text}</div>}
                        <div className="mt-1 flex items-center gap-2 text-[11px]">
                          <span className={`px-1.5 py-0.5 rounded ${s.status === "graded" ? "bg-emerald-50 text-emerald-700" : s.status === "returned" ? "bg-amber-50 text-amber-700" : "bg-secondary text-muted-foreground"}`}>{s.status}</span>
                          {s.score !== null && s.score !== undefined && <span className="font-mono-eos">Score: {s.score}</span>}
                          {s.feedback && <span className="text-muted-foreground italic">"{s.feedback}"</span>}
                        </div>
                      </div>
                      <Button variant="outline" onClick={() => openEdit("grade", s)} className="h-7 px-2 text-[11px]" data-testid={`grade-${s.submission_id}`}>Grade</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ----- Quizzes ----- */}
        {tab === "quizzes" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="quizzes-pane">
            <div className="lg:col-span-1 space-y-2">
              {quizzes.length === 0 && <div className="eos-card text-center text-sm text-muted-foreground">No quizzes yet.</div>}
              {quizzes.map(q => (
                <div key={q.quiz_id} onClick={() => setSelectedQuiz(q.quiz_id)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setSelectedQuiz(q.quiz_id); }} className={`text-left eos-card eos-card-hover cursor-pointer ${selectedQuiz === q.quiz_id ? "border-[hsl(244_53%_20%)]" : ""}`} data-testid={`quiz-${q.quiz_id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono-eos text-[11px] text-muted-foreground">{q.course_id}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${q.mode === "proctored" ? "bg-rose-50 text-rose-700" : q.mode === "graded" ? "bg-indigo-50 text-indigo-700" : "bg-secondary"}`}>{q.mode}</span>
                  </div>
                  <div className="font-medium text-sm mt-1">{q.title}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{q.time_limit_min} min · pass {q.passing_score}%</div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button onClick={(e) => { e.stopPropagation(); nav(`/app/quiz/${q.quiz_id}`); }} className="h-7 px-2 text-[11px] bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_40%)]" data-testid={`take-quiz-${q.quiz_id}`}>{t("lms.take_quiz")}</Button>
                    <Button variant="ghost" onClick={(e) => { e.stopPropagation(); removeQuiz(q); }} className="h-7 px-2 text-[10px] text-rose-600" data-testid={`del-quiz-${q.quiz_id}`}><Trash size={10} className="mr-1"/>Delete</Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="lg:col-span-2">
              {!selectedQuiz && <div className="eos-card text-center text-sm text-muted-foreground">Select a quiz to view its questions.</div>}
              {selectedQuiz && (
                <div className="eos-card" data-testid="questions-pane">
                  <div className="font-heading font-bold mb-3">{quizzes.find(q => q.quiz_id === selectedQuiz)?.title} — Questions</div>
                  <div className="space-y-2">
                    {questions.filter(q => q.quiz_id === selectedQuiz).map((q, i) => (
                      <div key={q.question_id} className="p-3 border border-border rounded-md" data-testid={`question-${q.question_id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="text-[11px] text-muted-foreground">Q{i + 1} · {q.type} · {q.marks} marks{q.outcome_code ? ` · ${q.outcome_code}` : ""}</div>
                            <div className="font-medium text-sm mt-1">{q.text}</div>
                            {q.options?.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {q.options.map((opt, j) => (
                                  <li key={j} className={`text-[12px] flex items-center gap-1.5 ${q.correct?.includes(opt) ? "text-emerald-700 font-medium" : "text-muted-foreground"}`}>
                                    {q.correct?.includes(opt) && <CheckCircle size={12} weight="fill"/>}
                                    {opt}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <Button variant="ghost" onClick={() => removeQuestion(q)} className="h-6 w-6 p-0 text-rose-600" data-testid={`del-question-${q.question_id}`}><Trash size={12}/></Button>
                        </div>
                      </div>
                    ))}
                    {questions.filter(q => q.quiz_id === selectedQuiz).length === 0 && <div className="text-sm text-muted-foreground italic">No questions yet. Click "Question" above to add one.</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ----- Live Classes ----- */}
        {tab === "live" && (
          <div className="eos-card p-0 overflow-hidden" data-testid="live-pane">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground">
                <tr>{["Course", "Title", "Start", "Duration", "Provider", "Host", "Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {liveSessions.map(s => (
                  <tr key={s.session_id} className="border-t border-border hover:bg-secondary/40" data-testid={`live-row-${s.session_id}`}>
                    <td className="px-4 py-3 font-mono-eos text-[12px]">{s.course_id}</td>
                    <td className="px-4 py-3 font-medium">{s.title}</td>
                    <td className="px-4 py-3 text-muted-foreground">{new Date(s.start_at).toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono-eos">{s.duration_min}m</td>
                    <td className="px-4 py-3"><span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{s.provider}</span></td>
                    <td className="px-4 py-3 text-[12px]">{s.host_name}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <a href={s.join_url} target="_blank" rel="noreferrer" className="text-[11px] underline text-[hsl(244_53%_20%)]" data-testid={`join-${s.session_id}`}>Join</a>
                        <Button variant="ghost" onClick={() => removeLive(s)} className="h-7 w-7 p-0 text-rose-600" data-testid={`del-live-${s.session_id}`}><Trash size={13}/></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {liveSessions.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-sm text-muted-foreground">No live sessions scheduled.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* ----- Catalog ----- */}
        {tab === "courses" && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map(c => (
              <div key={c.course_id} className="eos-card eos-card-hover relative" data-testid={`course-card-${c.course_id}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] px-2 py-0.5 rounded-md font-medium ${TYPE_CLS[c.type] || "bg-secondary"}`}>{c.type}</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-mono-eos text-muted-foreground mr-1">{c.course_id}</span>
                    <Button variant="ghost" onClick={() => openEdit("course", c)} className="h-6 w-6 p-0 rounded" title="Edit" data-testid={`edit-${c.course_id}`}><PencilSimple size={12}/></Button>
                    <Button variant="ghost" onClick={() => removeCourse(c)} className="h-6 w-6 p-0 rounded text-rose-600" title="Delete" data-testid={`delete-${c.course_id}`}><Trash size={12}/></Button>
                  </div>
                </div>
                <div className="font-heading font-bold text-lg mt-3 tracking-tight leading-snug">{c.title}</div>
                <div className="text-xs text-muted-foreground mt-1">{c.faculty} · {c.credits} credits (NEP {c.nep_credits})</div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{c.students} learners</span>
                    <span>{c.completion}% complete</span>
                  </div>
                  <div className="mt-1.5 h-1 bg-border rounded-full overflow-hidden">
                    <div className="h-full bg-[hsl(20_90%_48%)]" style={{ width: `${c.completion}%` }}/>
                  </div>
                </div>
              </div>
            ))}
            {courses.length === 0 && <div className="col-span-full p-10 text-center text-sm text-muted-foreground">No courses yet.</div>}
          </div>
        )}
      </div>

      <CrudModal
        open={modal.open}
        onClose={close}
        title={
          modal.kind === "course" ? (modal.editing ? "Edit course" : "New course") :
          modal.kind === "assignment" ? (modal.editing ? "Edit assignment" : "New assignment") :
          modal.kind === "module" ? "New module" :
          modal.kind === "lesson" ? "New lesson" :
          modal.kind === "outcome" ? "New outcome" :
          modal.kind === "quiz" ? "New quiz" :
          modal.kind === "question" ? "New question" :
          modal.kind === "live" ? "Schedule live session" :
          modal.kind === "submission" ? "Record submission" :
          modal.kind === "grade" ? "Grade submission" : ""
        }
        fields={
          modal.kind === "course" ? (modal.editing ? COURSE_FIELDS_EDIT : COURSE_FIELDS_NEW) :
          modal.kind === "assignment" ? ASG_FIELDS :
          modal.kind === "module" ? MODULE_FIELDS(courseOptions) :
          modal.kind === "lesson" ? LESSON_FIELDS(modules) :
          modal.kind === "outcome" ? OUTCOME_FIELDS(courseOptions) :
          modal.kind === "quiz" ? QUIZ_FIELDS(courseOptions) :
          modal.kind === "question" ? QUESTION_FIELDS(quizOptions) :
          modal.kind === "live" ? LIVE_FIELDS(courseOptions) :
          modal.kind === "submission" ? SUBMIT_FIELDS :
          modal.kind === "grade" ? GRADE_FIELDS : []
        }
        initial={
          modal.editing ||
          (modal.kind === "course" ? { credits: 4, nep_credits: 4, students: 0, completion: 0, type: "Core" } :
            modal.kind === "assignment" ? { status: "Open", submissions: 0, total_students: 0 } :
            modal.kind === "module" ? { course_id: modal.extra.course_id || "", order: 1, status: "draft" } :
            modal.kind === "lesson" ? { module_id: modal.extra.module_id || "", order: 1, content_type: "text", duration_min: 10 } :
            modal.kind === "outcome" ? { type: "CO" } :
            modal.kind === "quiz" ? { time_limit_min: 30, passing_score: 50, mode: "practice" } :
            modal.kind === "question" ? { quiz_id: modal.extra.quiz_id || "", type: "MCQ", marks: 1 } :
            modal.kind === "live" ? { duration_min: 60, provider: "meet" } :
            modal.kind === "submission" ? { student_name: "", student_id: "" } :
            modal.kind === "grade" ? { score: 0, status: "graded" } : {})
        }
        onSubmit={save}
        submitLabel={modal.editing ? "Save changes" : "Create"}
      />
      <AIQuizGenerator
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        courses={courses}
        assets={assets}
        onCreated={() => { setAiOpen(false); load(); setTab("quizzes"); }}
      />
    </div>
  );
}
